import { useQuery } from '@tanstack/react-query'
import { maintenanceApi, type MaintenanceState } from '../../utils/api'

const CACHE_KEY = 'maintenance_state'

// キャッシュを信用する上限。メンテ解除後に本物の障害が起きたとき、古い「メンテ中」を
// いつまでも出し続けないための頭打ち。実運用のメンテ枠より十分長く、かつ日をまたがない長さ。
const CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000

const DISABLED: MaintenanceState = { enabled: false, ends_at: null, message: null }

/**
 * メンテ中の状態だけを localStorage に残す。
 *
 * メンテ作業ではバックエンド自体を落とすことがあり、その間は状態を取得できない。
 * 直前に「メンテ中」を受け取っていれば、以降の取得失敗時もメンテ画面を出し続けられる。
 * 解除を受け取った時点で消すので、平常時に古い状態が残ることはない。
 */
function writeCache(state: MaintenanceState): void {
  try {
    if (state.enabled) {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ state, savedAt: Date.now() }))
    } else {
      localStorage.removeItem(CACHE_KEY)
    }
  } catch {
    // ストレージが使えない環境（プライベートモード等）では素通しする
  }
}

function readCache(): MaintenanceState | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { state, savedAt } = JSON.parse(raw) as { state: MaintenanceState; savedAt: number }
    if (!state?.enabled || Date.now() - savedAt > CACHE_MAX_AGE_MS) {
      localStorage.removeItem(CACHE_KEY)
      return null
    }
    return state
  } catch {
    return null
  }
}

/**
 * メンテナンス状態の取得。
 * queryKey: ['maintenance']
 *
 * セッション中に管理者がメンテを開始した場合も画面が切り替わるよう、定期的に取り直す。
 * 取得できないときは「メンテではない」と扱う（fail open）。メンテ状態が読めないことを
 * 理由にサービス全体を止めると、本物の障害と区別がつかなくなるため。
 * ただし直前にメンテ中を観測していた場合に限り、キャッシュを根拠にメンテ表示を維持する。
 */
export function useMaintenance() {
  const query = useQuery({
    queryKey: ['maintenance'],
    queryFn: async () => {
      const state = await maintenanceApi.get()
      writeCache(state)
      return state
    },
    // メンテの開始/終了に最大1分で追随する
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 1,
  })

  const state: MaintenanceState = query.data ?? readCache() ?? DISABLED

  return {
    /** メンテ画面に差し替えるべきか */
    active: state.enabled,
    endsAt: state.ends_at,
    message: state.message,
    /** 初回取得が終わるまで true（ゲート判定を待たせるために使う） */
    loading: query.isLoading,
  }
}
